import { useCallback, useMemo, type FC } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ShipmentDto } from '@/api';
import {
  Alert,
  DebouncedSearchInput,
  Input,
  Select,
  Table,
  TablePagination,
  useTableSort,
  type TableColumn,
} from '@/ui';

import {
  ShipmentBadges,
  ShipmentOriginBadge,
} from '../../components/ShipmentBadges';
import { ShipmentLinesCell } from '../../components/ShipmentLinesCell';
import { resolveApiErrorMessage } from '../../lib/apiError';
import { formatDate, formatMoney, toNumber } from '../../lib/format';
import {
  FINISHED_SHIPMENT_STATUSES,
  SHIPMENT_STATUSES,
} from '../../lib/shipmentStatus';
import { formatSort } from '../../lib/tableSort';
import { useShipmentHistory } from './api/queries';
import {
  PAGE_SIZE,
  resolveHistorySearch,
  type HistorySearch,
  type HistoryStatusFilter,
} from './lib/searchParams';

const ROUTE_ID = '/_app/history' as const;
const ROUTE_PATH = '/history' as const;

/*
 * История отгрузок - закрытые отгрузки (завершённые, возвраты, отмены), свежие
 * сверху: и созданные у нас, и унаследованные от заказов 1С (ADR-012). Фильтры -
 * клиент, период плановой даты, статус, происхождение, расхождения с 1С. Строка
 * ведёт в карточку. Выручка и маржа появятся с потоком реализаций - пока сумма
 * отгрузки без НДС.
 */
export const ShipmentHistoryPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const resolved = resolveHistorySearch(search);

  const setSearch = useCallback(
    (patch: Partial<HistorySearch>, resetPage = true) => {
      void navigate({
        from: ROUTE_PATH,
        to: ROUTE_PATH,
        search: (previous: HistorySearch) => ({
          ...previous,
          ...patch,
          ...(resetPage ? { page: undefined } : null),
        }),
      });
    },
    [navigate],
  );

  const { toggleSort } = useTableSort(resolved.sort, (next) =>
    setSearch({ sort: formatSort(next) }),
  );

  const { data, isPending, isError, error } = useShipmentHistory(resolved);

  const total = toNumber(data?.totalRecords ?? 0);
  const totalPages = Math.max(1, toNumber(data?.totalPages ?? 1));
  const firstOnPage = total === 0 ? 0 : (resolved.page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(resolved.page * PAGE_SIZE, total);

  const columns = useMemo<ReadonlyArray<TableColumn<ShipmentDto>>>(
    () => [
      {
        id: 'number',
        header: '№',
        mono: true,
        width: 'w-24',
        isSortable: true,
        cell: (shipment) => (
          <span className="font-medium text-ink-700">{shipment.number}</span>
        ),
      },
      {
        id: 'partner',
        header: 'Клиент',
        cell: (shipment) => (
          <span className="font-[550] text-ink-900">
            {shipment.partnerName}
          </span>
        ),
      },
      {
        id: 'plannedDate',
        header: 'Дата отгрузки',
        width: 'w-36',
        isSortable: true,
        cell: (shipment) => formatDate(shipment.plannedDate),
      },
      {
        id: 'order',
        header: 'Заказ 1С',
        width: 'w-32',
        hideBelow: 'md',
        cell: (shipment) =>
          shipment.customerOrderNumber ?? (
            <span className="text-ink-400">-</span>
          ),
      },
      {
        id: 'lines',
        header: 'Товар',
        hideBelow: 'md',
        cell: (shipment) => <ShipmentLinesCell lines={shipment.lines} />,
      },
      {
        id: 'amount',
        header: 'Сумма без НДС',
        width: 'w-40',
        align: 'right',
        cell: (shipment) => formatMoney(toNumber(shipment.amount)),
      },
      {
        id: 'status',
        header: 'Статус',
        width: 'w-64',
        // Источник - в одной строке бейджей со статусом, как в «Отгрузках в работе».
        cell: (shipment) => (
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <ShipmentBadges
              className="contents"
              status={shipment.status}
              handoverState={shipment.handoverState}
              onecDiscrepancies={shipment.onecDiscrepancies}
            />
            <ShipmentOriginBadge
              origin={shipment.origin}
              customerOrderNumber={shipment.customerOrderNumber}
              isForecast={shipment.isForecast}
            />
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-white px-3.5 py-3">
        <DebouncedSearchInput
          className="min-w-[220px] flex-1"
          placeholder="Поиск по клиенту..."
          aria-label="Поиск по клиенту"
          value={resolved.search}
          onCommit={(value) => setSearch({ search: value || undefined })}
        />
        <div className="flex items-center gap-1.5 text-xs text-ink-500">
          <span>с</span>
          <Input
            type="date"
            aria-label="Дата отгрузки с"
            className="w-[132px]"
            value={resolved.from}
            max={resolved.to || undefined}
            onChange={(event) =>
              setSearch({ from: event.target.value || undefined })
            }
          />
          <span>по</span>
          <Input
            type="date"
            aria-label="Дата отгрузки по"
            className="w-[132px]"
            value={resolved.to}
            min={resolved.from || undefined}
            onChange={(event) =>
              setSearch({ to: event.target.value || undefined })
            }
          />
        </div>
        <Select
          aria-label="Статус"
          value={resolved.status}
          onChange={(event) => {
            const status = event.target.value as HistoryStatusFilter;
            setSearch({ status: status === 'finished' ? undefined : status });
          }}
        >
          <option value="finished">Все закрытые</option>
          {FINISHED_SHIPMENT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {SHIPMENT_STATUSES[status].label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Происхождение"
          value={resolved.origin}
          onChange={(event) => {
            const origin = event.target.value;
            setSearch({
              origin:
                origin === 'Culture' || origin === 'Onec' ? origin : undefined,
            });
          }}
        >
          <option value="all">Все отгрузки</option>
          <option value="Culture">Созданные в Culture</option>
          <option value="Onec">По заказам из 1С</option>
        </Select>
        <label className="inline-flex items-center gap-1.5 text-[13px] text-ink-700">
          <input
            type="checkbox"
            checked={resolved.discrepancies}
            onChange={(event) =>
              setSearch({ discrepancies: event.target.checked || undefined })
            }
          />
          С расхождениями с 1С
        </label>
      </div>

      {isError ? (
        <Alert tone="error">
          Не удалось загрузить историю отгрузок: {resolveApiErrorMessage(error)}
          .
        </Alert>
      ) : (
        <Table
          columns={columns}
          rows={data?.items ?? []}
          getRowKey={(shipment) => shipment.id}
          onRowClick={(shipment) =>
            void navigate({
              to: '/shipments/$shipmentId',
              params: { shipmentId: shipment.id },
            })
          }
          isLoading={isPending}
          sort={resolved.sort}
          onSortChange={toggleSort}
          emptyLabel="Закрытых отгрузок по выбранным фильтрам нет"
          footer={
            <TablePagination
              page={resolved.page}
              totalPages={totalPages}
              onPageChange={(page) =>
                setSearch({ page: page === 1 ? undefined : page }, false)
              }
              summary={
                total === 0
                  ? 'Ничего не найдено'
                  : `Показаны ${firstOnPage}-${lastOnPage} из ${total}`
              }
            />
          }
        />
      )}
    </>
  );
};
